import { captureMessage } from "@sentry/browser";

const STORAGE_KEY = "checkout_load_timestamps";
const WINDOW_MS = 20_000;
const RELOAD_THRESHOLD = 3;

/**
 * Detects a rapid page-reload / crash loop on checkout and reports it to Sentry.
 *
 * The WebKit content-process crash behind iOS Safari's "A problem repeatedly
 * occurred" cannot be caught by the JS SDK — the process dies before it can
 * report. The one observable signal is its aftermath: the page loading many
 * times in quick succession. We record load timestamps in sessionStorage
 * (per-tab, survives reloads, cleared on tab close); when checkout loads
 * >= RELOAD_THRESHOLD times within WINDOW_MS we emit a captureMessage tagged
 * with the client context (in-app browser, iOS version, connectivity).
 *
 * @param pathname current path (injectable for tests; defaults to window.location.pathname)
 */
export const detectReloadLoop = (
  pathname: string = typeof window !== "undefined" ? window.location.pathname : ""
): void => {
  if (!pathname.includes("/checkout")) return;

  try {
    const now = Date.now();
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    const timestamps = (Array.isArray(parsed) ? parsed : [])
      .filter((t): t is number => typeof t === "number" && now - t < WINDOW_MS)
      .concat(now);

    if (timestamps.length >= RELOAD_THRESHOLD) {
      captureMessage("Checkout reload/crash loop detected", {
        level: "error",
        extra: { reloadCount: timestamps.length, windowMs: WINDOW_MS, pathname },
      });
      // Reset so we report once per burst rather than on every subsequent load.
      window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify([]));
      return;
    }

    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(timestamps));
  } catch {
    // sessionStorage / JSON can throw in privacy modes or sandboxed in-app
    // browsers. Detection is best-effort; never let it break app boot.
  }
};
